import { useGeneratePhotographiesPdf } from "@/api/pdf";
import { useGetTinyPhotographies } from "@/api/photography/photography";
import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import i18n, { SupportedLocale } from "@/i18n";
import { getDefaultImage } from "@/lib/image";
import { PropsWithChildren, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PrintDrawer from "../print/PrintDrawer";
import SelectedRecord from "../print/SelectedRecord";
import { Checkbox } from "../ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import { Separator } from "../ui/separator";
import {
  PhotographyPrintSettings,
  simpleTemplateFields,
} from "./photographyPrintSettings";

type PhotographyPrintDrawerProps = PropsWithChildren<{
  selectedPhotographiesIds: string[];
}>;

type FormValues = {
  settings: PhotographyPrintSettings;
};

const PhotographyPrintDrawer = ({
  selectedPhotographiesIds,
}: PhotographyPrintDrawerProps) => {
  const { t } = useTranslation();
  const { prismaOrderByQueryContent } = useAdvancedSearch();

  const form = useForm<FormValues>({
    defaultValues: {
      settings: simpleTemplateFields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: false,
        }),
        {} as PhotographyPrintSettings,
      ),
    },
  });
  const { mutateAsync: generatePhotographiesPdf, isPending } =
    useGeneratePhotographiesPdf();
  const formId = useId();
  const [previewURL, setPreviewURL] = useState<string | null>(null);

  // TECH_DEBT: better handling of attachments
  // TECH_DEBT: also this triggers as soon as items are selected, not when the drawer actually opens
  const { data: selectedPhotographies = [], isFetching } =
    useGetTinyPhotographies({
      where: {
        id: { in: selectedPhotographiesIds },
      },
      orderBy: prismaOrderByQueryContent,
    });

  const onSubmit = async (data: FormValues) => {
    const file = await generatePhotographiesPdf({
      lang: i18n.language as SupportedLocale,
      template: "simple",
      photographyIds: selectedPhotographiesIds,
      settings: data.settings,
      orderBy: prismaOrderByQueryContent,
    });
    const url = URL.createObjectURL(file);
    setPreviewURL(url);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} id={formId}>
        <PrintDrawer
          formId={formId}
          submitButtonProps={{ isLoading: isPending }}
          previewUrl={previewURL}
          setPreviewUrl={setPreviewURL}
        >
          <div className="flex flex-col gap-6">
            <div className="space-y-4">
              <FormLabel>{t("printOptions")}</FormLabel>
              {simpleTemplateFields.map((option) => (
                <FormField
                  key={option}
                  control={form.control}
                  name={`settings.${option}`}
                  render={({ field }) => (
                    <FormItem className="flex flex-row gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value as boolean}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel>{t(option)}</FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>

          <Separator className="my-4" />

          <div className="flex flex-col gap-4">
            <span className="font-semibold font-[Raleway] text-base">
              {t("photographiesIncluded")}
            </span>

            {!isFetching &&
              selectedPhotographies.map(
                ({
                  id,
                  photosDescriptionNumeroEnregistrement,
                  photosDescriptionTitre,
                  attachments,
                }) => {
                  const image = getDefaultImage(
                    attachments,
                    "photography",
                    "publicUrl",
                  );

                  return (
                    <SelectedRecord
                      key={id}
                      image={image}
                      title={`${photosDescriptionNumeroEnregistrement} - ${photosDescriptionTitre}`}
                    />
                  );
                },
              )}
          </div>
        </PrintDrawer>
      </form>
    </Form>
  );
};

export default PhotographyPrintDrawer;
